<template>
    <div>
        <SToolbar>
            <TypIcon v-if="record" :address-record="record" class="mr-1" />
            <v-toolbar-title v-if="!record">{{ $t('adresse') }}</v-toolbar-title>
            <v-toolbar-title v-else>{{ $t(record.type) }}</v-toolbar-title>
        </SToolbar>
        <v-skeleton-loader v-if="!record" type="article, actions" />
        <v-card v-else height="var(--usable-height)" class="main-grid">
            <LoadingOverlay :value="saving" absolute />

            <!-- Header with Buttons -->
            <div class="top-header">
                <light-toolbar dense>
                    <v-toolbar-title>
                        <LegalTypeIcon :address-record="record" class="mr-1" />
                        <span v-if="record.legal_type === 'person'">{{ $t('person') }}: </span>
                        <span v-else-if="record.legal_type === 'organization'">{{ $t('organization') }}: </span>
                        <span v-else>({{ $t('no_type') }}): </span>
                        <strong>{{ record.title || `${record.first_name ?? ''} ${record.surname ?? ''}` }}</strong>
                    </v-toolbar-title>
                    <v-spacer />
                    <AddressIconBar :item="record" hide-status-icon />
                    <KpIcon :icon-cls="record.status_icon" :color="record.status_icon_color" left />
                    <span class="grey--text">{{ record.humanReadableStatus }}</span>
                </light-toolbar>

                <v-toolbar dense class="elevation-0" color="primary white--text">
                    <v-slide-group next-icon="ic-20 ic-next ic-col-white" prev-icon="ic-20 ic-prev ic-col-white">
                        <v-btn
                            v-if="!hidePreview"
                            color="primary"
                            class="ml-1"
                            depressed
                            :href="getDetailPreviewUrl()"
                            target="_blank"
                        >
                            <KpIcon icon-cls="preview" color="white" left />
                            {{ $t('prev_detail') }}
                        </v-btn>
                        <span v-if="!hidePreview">|</span>
                        <IconBtn
                            v-if="showKulturplatzPublishBtns"
                            color="primary"
                            icon-cls="published"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :disabled="!isValid"
                            :tooltip="$t('allow_kulturplatz')"
                            @click="publishDlg = true"
                        />
                        <IconBtn
                            v-if="showKulturplatzPublishBtns"
                            color="primary"
                            icon-cls="rejected"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :disabled="!isValid"
                            :tooltip="$t('deny_kulturplatz')"
                            @click="unpublishDlg = true"
                        />
                        <span v-if="showKulturplatzPublishBtns">|</span>
                        <v-btn v-if="userIsAdmin()" color="primary" depressed @click="assignOwnerDlg = true">
                            <KpIcon icon-cls="user" left color="white" disabled-color="grey" />
                            {{ $t('assign_owner') }}
                        </v-btn>
                    </v-slide-group>
                    <v-spacer />
                    <v-btn
                        v-if="userIsStaff()"
                        class="ml-1"
                        :color="record?.import_protection ? 'primaryToggled' : 'primary'"
                        depressed
                        @click="onInput('import_protection', !record?.import_protection, true)"
                    >
                        <KpIcon icon-cls="importsafe" color="white" left />
                        {{ $t('import_protection') }}
                    </v-btn>
                </v-toolbar>
            </div>

            <!-- Navigation -->
            <div class="navigation">
                <v-tabs v-model="activeItem" vertical background-color="transparent" hide-slider>
                    <v-tab
                        v-for="item in navItems"
                        :key="item.id"
                        :disabled="!Number(record.id)"
                        exact
                        class="nav-tab"
                        :to="{ name: item.routeName }"
                    >
                        <KpIcon :icon-cls="`${item.icon}`" color="grey" active-color="black" class="ma-1" left />
                        {{ item.title }}
                    </v-tab>
                </v-tabs>
            </div>

            <!-- Main content -->
            <div class="content">
                <router-view
                    ref="activeChild"
                    v-model="isValid"
                    :record="record"
                    :specific-record="specificRecord"
                    :loading="saving"
                    @save-request="onSave"
                    @saving="saving = $event"
                    @featured-image-created="onFeaturedImagesCreated"
                    @assign-event-organizer="onAssignEo"
                ></router-view>
            </div>

            <!-- footer -->
            <div class="bottom-toolbar d-flex justify-end">
                <PlainBtn class="mr-1" @click="onCancel">
                    {{ backRoute?.displayText ? $t('back_to', { text: backRoute?.displayText }) : $t('schliessen') }}
                </PlainBtn>
                <PlainBtn class="mr-1" :disabled="!isValid" @click="onBack">{{ $t('back') }}</PlainBtn>
                <ActionBtn v-if="!lastItem" :disabled="!isValid" :loading="saving" @click="onSaveAndNext">
                    <KpIcon icon-cls="save" color="white" left />{{ $t('save_next') }}
                </ActionBtn>
            </div>
        </v-card>

        <ConfirmDialog
            v-model="publishDlg"
            :title="$t('allow_kulturplatz')"
            :message="$t('allow_kulturplatz_dlg_text')"
            :confirm-label="$t('yes_with_mail')"
            :confirm-alternate-label="$t('yes')"
            @confirm="publish(true)"
            @confirm-alternate="publish(false)"
        />
        <UnpublishDialog v-model="unpublishDlg" @yes="unpublish" />
        <SelectOwnerDialog
            v-model="assignOwnerDlg"
            context="Adress-Owner anpassen"
            :address="record"
            @selected="onCreatorSelect"
        ></SelectOwnerDialog>
    </div>
</template>

<script>
import { Address, AddressCollection } from '@/models/Address';
import { Location, LocationCollection } from '@/models/Location';
import { EventOrganizer, EventOrganizerCollection } from '@/models/EventOrganizer';
import { errToStr, setBrowserTitle, shortenString } from '@/helpers/tools';
import { instance as api } from '@/helpers/api';
import { mapActions } from 'vuex';
import { frontendUrl, userInfo, globalParam, kulturplatzModuleEnabled } from '@/helpers/globals';
import UnpublishDialog from '@/components/address/UnbublishDialog.vue';
import TypIcon from '@/components/address/TypIcon.vue';
import LegalTypeIcon from '@/components/address/LegalTypeIcon.vue';
import { userIsAdmin, userIsStaff } from '@/helpers/auth';
import SelectOwnerDialog from '@/components/SelectOwnerDialog.vue';
import BackRouteMixin from '@/mixins/backroute';
import AddressIconBar from '@/components/address/AddressIconBar.vue';

export default {
    name: 'AddressEdit',
    components: {
        UnpublishDialog,
        TypIcon,
        LegalTypeIcon,
        SelectOwnerDialog,
        AddressIconBar,
    },
    mixins: [BackRouteMixin],

    async beforeRouteUpdate(to, from, next) {
        await this.onSave();
        if (to.params.id !== from.params.id) {
            this.record = null;
        }
        this.loadRecord(to.params.id);
        next();
    },

    data() {
        return {
            unpublishDlg: false,
            publishDlg: false,
            assignOwnerDlg: false,
            saving: false,
            record: null,
            // An Address record can be accompanied by its specific record:
            // Location or EventOrganizer: It is side-loaded after the Address
            // record is loaded:
            specificRecord: null,
            isValid: true,
            activeItem: null,
            navItems: [
                {
                    id: 'basisdaten',
                    routeName: 'address-edit',
                    icon: 'target',
                    title: this.$t('stammdaten'),
                },
                kulturplatzModuleEnabled()
                    ? {
                          id: 'image',
                          routeName: 'address-edit-kulturplatz-entry',
                          icon: 'world',
                          title: this.$t('kulturplatz_entry'),
                      }
                    : null,
                {
                    id: 'metadata',
                    routeName: 'address-edit-metadata',
                    icon: 'info',
                    title: this.$t('metadata'),
                },
                userIsStaff()
                    ? {
                          id: 'relations',
                          routeName: 'address-edit-relations',
                          icon: 'link',
                          title: this.$t('relationen'),
                      }
                    : null,
                userIsStaff()
                    ? {
                          id: 'changelog',
                          routeName: 'address-edit-changelog',
                          icon: 'log',
                          title: this.$t('changelog'),
                      }
                    : null,
            ].filter(Boolean),
        };
    },

    computed: {
        activeNavItem() {
            const actRouteName = this.$route.name;
            const items = this.navItems.filter((navItem) => navItem.routeName === actRouteName);
            if (items?.length > 0) {
                return items[0];
            }
            return null;
        },
        lastItem() {
            const routeName = this.$route.name;
            return this.navItems[this.navItems.length - 1].routeName === routeName;
        },
        browserTitle() {
            const parts = [];
            if (this.record?.displayTitle) {
                parts.push(shortenString(this.record?.displayTitle, 30), this.activeNavItem?.displayTitle);
            } else {
                parts.push(this.$t('create_entry'));
            }
            parts.push(this.$t('address'));
            return parts.filter(Boolean).join(' - ');
        },
        hidePreview() {
            return globalParam('address.hide_preview');
        },

        showKulturplatzPublishBtns() {
            return userIsStaff() && kulturplatzModuleEnabled();
        },
    },

    watch: {
        browserTitle: {
            handler(newValue) {
                setBrowserTitle(newValue);
            },
            immediate: true,
        },
    },

    async beforeDestroy() {
        if (!this.record?.isPhantom()) {
            await this.onSave();
        }
    },

    mounted() {
        this.loadRecord(this.$route.params.id);
        this.updateActiveNavItem(this.$route);
    },

    methods: {
        ...mapActions(['runAsync']),
        userIsStaff,
        userIsAdmin,
        async loadRecord(id) {
            // create a new record if requested, or load an existing one:
            if (id === 'new' || !Number(id)) {
                this.initNewRecord();
            } else {
                // load existing record:
                try {
                    const collection = new AddressCollection();
                    await this.runAsync(() => collection.query({ id: id }));
                    const record = collection.first();
                    if (record) {
                        this.record = record;
                        if (this.record.type !== 'address') {
                            await this.loadSpecificRecord(this.record);
                        }
                    } else {
                        this.record = null;
                    }
                    this.validateForm();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                }
            }
        },

        initNewRecord() {
            let legal_type = this.$route.query.legal_type || null;
            let type = this.$route.query.type || null;
            if (['person', 'organization'].indexOf(legal_type) < 0) {
                legal_type = null;
            }
            if (['location', 'event_organizer'].indexOf(type) < 0) {
                type = 'address';
            }
            this.record = new Address({ type, legal_type });
            if (type === 'event_organizer') {
                this.specificRecord = new EventOrganizer();
            }
            if (type === 'location') {
                this.record.legal_type = 'organization';
                this.specificRecord = new Location({
                    event_organizer_id: userIsStaff() ? null : userInfo().event_organizer_id,
                    legal_type: 'organization',
                });
            }
            // premature return in case of new record:
            this.validateForm();
        },

        /**
         * An address is the main class which can be accompanied by its child "companion" record
         * Location or EventOranizer: If the Address has such an accompanioning record,
         * we load it here, too.
         * It is then available to the view in specificRecord.
         */
        async loadSpecificRecord(addressRec) {
            if (!addressRec) {
                return;
            }
            let collection = null;
            switch (addressRec.type) {
                case 'location':
                    collection = new LocationCollection();
                    break;
                case 'event_organizer':
                    collection = new EventOrganizerCollection();
                    break;
                default:
                    return;
            }
            collection.setFilter('address_id', addressRec.get('id'));
            await this.runAsync(() => collection.query({ showAllLocations: true }));
            const record = collection.first();
            if (record) {
                this.specificRecord = record;
            }
        },

        async validateForm() {
            await this.$nextTick();
            if (this.$refs.activeChild.validateForm) {
                this.$refs.activeChild.validateForm();
            }
        },

        async onSave(options = {}) {
            const reload = !!options.reload;
            if (!this.record) {
                return;
            }

            this.saving = true;

            // give the child a change to do things before we save:
            // e.g. save block contents:
            if (this.$refs.activeChild.beforeSave) {
                const res = await this.$refs.activeChild.beforeSave();
                if (res === false) {
                    this.saving = false;
                    return false;
                }
            }

            // do we need to save the main record?
            const isNew = !this.record.id;
            const saveNeeded = !this.record.id || this.record.changed();

            try {
                if (saveNeeded) {
                    await this.runAsync(() => this.record.save());
                }
                // If needed, we also save the size loaded specific record:
                if (this.specificRecord && (!this.specificRecord.id || this.specificRecord.changed())) {
                    this.specificRecord.address_id = this.record.get('id');
                    await this.runAsync(() => this.specificRecord.save());
                }
                if (isNew) {
                    // when a new rec is created, replace the route...
                    this.$router.replace({ name: 'address-edit', params: { id: this.record.id } });
                } else if (reload) {
                    document.location.reload();
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },

        async onSaveAndNext() {
            const res = await this.onSave();
            if (res === false) {
                return;
            }

            if (this.activeNavItem) {
                const nextIndex = this.navItems.indexOf(this.activeNavItem) + 1;
                if (nextIndex > 0 && this.navItems.length > nextIndex) {
                    this.$router.push({ name: this.navItems[nextIndex].routeName });
                }
            }
        },

        onCancel() {
            let route = { name: 'address-all' };
            if (this.getBackRouteConfig()?.route) {
                route = this.getBackRouteConfig().route;
            } else {
                // depending on the type of address, we route back to a hopefully useful route:
                if (this.record.legal_type === 'person') {
                    route = { name: 'address-people' };
                }
                if (this.record.legal_type === 'organization') {
                    route = { name: 'address-organizations' };
                }
            }

            return this.$router.push(route);
        },

        async onBack() {
            await this.onSave();

            if (this.activeNavItem) {
                const nextIndex = this.navItems.indexOf(this.activeNavItem) - 1;
                if (nextIndex >= 0) {
                    this.$router.push({ name: this.navItems[nextIndex].routeName });
                }
            }
        },

        updateActiveNavItem(route) {
            if (route) {
                this.activeItem = this.getActNavItemIndex(route);
            }
        },

        getActNavItemIndex(route) {
            const routeName = route.name;
            for (const i in this.navItems) {
                if (this.navItems[i].routeName === routeName) {
                    return i;
                }
            }
            return 0;
        },

        getDetailPreviewUrl() {
            const id = this.record ? this.record.id : null;
            return frontendUrl(`/kulturplatz/${id}`, { preview: 'enable' });
        },

        onFeaturedImagesCreated() {
            this.onSave();
        },

        async publish(withMailConfirmation) {
            this.publishDlg = false;
            await this.onSave();
            await this.updateStatus('in_union', null, withMailConfirmation);
        },

        async unpublish(opts) {
            opts = opts || {};
            this.unpublishDlg = false;
            await this.onSave();
            await this.updateStatus('rejected', opts.reason, !!opts.reason);
        },

        async updateStatus(status, reason, withMailConfirmation) {
            this.saving = true;

            try {
                await api().post('/Address/status', {
                    address_id: this.record.id,
                    status: status,
                    reason: reason,
                    with_mail: withMailConfirmation === true,
                });
                document.location.reload();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.saving = false;
            }
        },

        /**
         * We can only assign a new event organizer:
         * - on locations
         * - when the user is a staff member
         */
        onAssignEo(eventOrganizer) {
            if (this.record.type === 'location' && this.specificRecord && userIsStaff()) {
                this.specificRecord.set('event_organizer_id', eventOrganizer ? eventOrganizer.get('id') : null);
                this.onSave({ reload: true });
            }
        },

        async onCreatorSelect(user) {
            if (user) {
                try {
                    this.assignOwnerDlg = false;
                    this.saving = true;
                    await this.record.storeCreator(user);
                    await this.loadRecord(this.record.id);
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.saving = false;
                }
            }
        },

        getBrowserTitle() {
            return this.browserTitle;
        },

        onInput(field, value) {
            this.record.set(field, value);
        },
    },
};
</script>

<style lang="scss" scoped>
.main-grid {
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-columns: 200px 1fr;
    grid-template-areas:
        'header header'
        'nav content'
        'nav bottom';
}

.top-header {
    grid-area: header;
}
.navigation {
    grid-area: nav;
    background-color: var(--toolbar-bg-color);
    padding: 12px;
    border-right: 1px solid var(--gray-border-color);
}
.content {
    grid-area: content;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    overflow: hidden;
}

.bottom-toolbar {
    grid-area: bottom;
    background-color: var(--toolbar-bg-color);
    padding: 10px 27px;
}

.nav-tab {
    height: initial !important;
    justify-content: start !important;
    padding: 5px 3px;
    margin: 3px;
    text-transform: none !important;
    font-weight: normal;
    font-size: 1rem;
    letter-spacing: 0;
    &.v-tab--active {
        color: var(--nearblack-color) !important;
    }
}
</style>
